/**
 * Frontend Logging Utility
 *
 * Provides structured logging for the frontend application.
 * Console statements are only active in development mode.
 * In production, errors can be sent to error tracking services.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const isDev = import.meta.env.DEV;

interface Logger {
  debug: (message: string, ...args: unknown[]) => void;
  info: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  error: (message: string, ...args: unknown[]) => void;
}

/**
 * Application logger
 *
 * Usage:
 * ```typescript
 * import { logger } from '../utils/logger';
 *
 * logger.debug('User action', { action: 'click', button: 'submit' });
 * logger.info('Data loaded', { recordCount: data.length });
 * logger.warn('Deprecated feature used', { feature: 'oldApi' });
 * logger.error('API request failed', { error, endpoint: '/api/carriers' });
 * ```
 */
export const logger: Logger = {
  debug: (message, ...args) => {
    if (isDev) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },
  info: (message, ...args) => {
    if (isDev) {
      console.info(`[INFO] ${message}`, ...args);
    }
  },
  warn: (message, ...args) => {
    console.warn(`[WARN] ${message}`, ...args);
  },
  error: (message, ...args) => {
    console.error(`[ERROR] ${message}`, ...args);

    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    // Example:
    // if (!isDev && window.Sentry) {
    //   window.Sentry.captureException(args[0]);
    // }
  },
};

/**
 * Performance logger for timing operations
 */
export const perfLogger = {
  start: (label: string): number => {
    const startTime = performance.now();
    if (isDev) {
      console.time(label);
    }
    return startTime;
  },

  end: (label: string, startTime: number): void => {
    const duration = performance.now() - startTime;
    if (isDev) {
      console.timeEnd(label);
      if (duration > 1000) {
        logger.warn(`Slow operation: ${label}`, { duration: `${duration.toFixed(2)}ms` });
      }
    }
  },
};

export default logger;
